import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {detailsExpand} from '../../animations';
import {EmailDTO} from 'src/app/modules/pbm/models/email/EmailDTO';
import {TableControl} from '../../models/TableControl';
import {CommunicationTriggerType} from '../../types/communication-trigger-type.enum';

export enum TableColumns {
  Icons = 'icons',
  EmailId = 'emailId',
  FromAddress = 'fromAddress',
  ToAddress = 'toAddress',
  Subject = 'subject',
  CommunicationTriggerType = 'communicationTriggerType',
  TriggerId = 'triggerId',
  IsSent = 'isSent',
  PdfReport = 'pdf', // FE usage
}

export enum IconName {
  Send = 'send',
  Reset = 'reset',
}

@Component({
  selector: 'greydom-email-table',
  templateUrl: './email-table.component.html',
  styleUrls: [],
  animations: [detailsExpand]
})
export class EmailTableComponent  {
  readonly tableColumns = TableColumns;
  readonly IconName = IconName;
  readonly emailDisplayedColumns: string[] = Object.values<TableColumns>(TableColumns);

  @Input() emailDataSource: MatTableDataSource<EmailDTO>;
  @Input() emailTableControl: TableControl;
  @Input() dateTimeFormat: string;
  @Input() emailExpandedRow: EmailDTO | null;
  @Input() emailOverrideFormControl: FormControl<string>;

  @Output() sortData = new EventEmitter<Sort>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() rowClicked = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() sendEmailConfirmed = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() toggleIcons = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() editAddress = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() cancelEditAddress = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() clickResetEmailConfirmed = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() clickToggleResetEmailIcons = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() saveAddress = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();
  @Output() duplicate = new EventEmitter<{ duplicate: boolean, row: EmailDTO }>();
  @Output() generatePdfReport = new EventEmitter<{ event: MouseEvent, row: EmailDTO }>();

  @ViewChild('emailPaginator', {static: false}) emailPaginator: MatPaginator;


  onSortData(event: Sort): void {
    this.sortData.emit(event);
  }

  onPageChanged(event: PageEvent): void {
    this.pageChanged.emit(event);
  }

  onRowClick(event: MouseEvent, row: EmailDTO): void {
    this.rowClicked.emit({event, row});
  }

  onClickSendEmailConfirmed(event: MouseEvent, row: EmailDTO): void {
    this.sendEmailConfirmed.emit({event, row});
  }

  onClickToggleResetEmailIcons(event: MouseEvent, row: EmailDTO): void {
    this.clickToggleResetEmailIcons.emit({event, row});
  }

  onClickToggleIcons(event: MouseEvent, row: EmailDTO): void {
    this.toggleIcons.emit({event, row});
  }
  onClickResetEmailConfirmed(event: MouseEvent, row: EmailDTO): void {
    this.clickResetEmailConfirmed.emit({event, row});
  }

  onClickCancelEditAddress(event: MouseEvent, row: EmailDTO): void {
    this.cancelEditAddress.emit({event, row});
  }

  onClickEditAddress(event: MouseEvent, row: EmailDTO): void {
    this.editAddress.emit({event, row});
  }

  onClickSaveAddress(event: MouseEvent, row: EmailDTO): void {
    this.saveAddress.emit({event, row});
  }

  onClickDuplicate(duplicate: boolean, row: EmailDTO): void {
    this.duplicate.emit({duplicate, row});
  }

  onClickGeneratePdfReport(event: MouseEvent, row: EmailDTO): void {
    this.generatePdfReport.emit({event, row});
  }

  getToAddress(row: EmailDTO): string {
    return row.editedToAddress || row.toAddress;
  }

  showIcon(row: EmailDTO, iconName: IconName): boolean {
    switch (iconName) {
    case IconName.Send:
      return (
        !row[TableColumns.IsSent] &&
        !row.sendEmailChecked &&
        !row.resetEmailConfirmation
      );
    case IconName.Reset:
      return (
        row[TableColumns.EmailId] &&
        !(row[TableColumns.CommunicationTriggerType]?.name === CommunicationTriggerType.ManualTriggerType) &&
        !row.sendEmailChecked &&
        !row.resetEmailConfirmation
      );
    default:
      return false;
    }
  }
}
